import { API_BASE_URL } from "./api/config";
import { IDENTITY_KEY, setToken, getToken, clearToken } from "./api/httpClient";

// Connexion back-office SANS Firebase : le backend Spring (health-ssb7)
// expose POST /admin/auth/login { email, password }. L'email doit figurer
// dans ADMIN_EMAILS et le mot de passe est le secret ADMIN_PASSWORD (variables
// d'environnement Render). Le backend renvoie { token, email, expiresAt } :
// un JWT signé (claim adm=true, 8 h) envoyé ensuite en Bearer sur /admin/**.
// Pas de rafraîchissement : à expiration, 401 → retour à l'écran de login.

export const LOGIN_PATH = "admin/auth/login";

export const INVALID_CREDENTIALS_MESSAGE = "Email ou mot de passe invalide";
export const TOO_MANY_ATTEMPTS_MESSAGE =
  "Trop de tentatives. Réessayez dans 15 minutes.";
export const NETWORK_ERROR_MESSAGE =
  "Serveur injoignable. Vérifiez votre connexion et réessayez.";

const persistIdentity = (email, token) => {
  const identity = {
    id: email,
    fullName: email,
    email,
    role: "ADMIN",
    permissions: ["ADMIN"],
  };
  // Jeton : mémoire + sessionStorage (onglet). Identité (non secrète) : sessionStorage.
  setToken(token);
  try {
    sessionStorage.setItem(IDENTITY_KEY, JSON.stringify(identity));
  } catch (_) {}
};

const clearSession = () => {
  clearToken();
  try {
    sessionStorage.removeItem(IDENTITY_KEY);
  } catch (_) {}
};

const readIdentity = () => {
  try {
    const raw = sessionStorage.getItem(IDENTITY_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

// Décode la payload d'un JWT (sans vérifier la signature : c'est le rôle du
// backend) pour lire son expiration. Renvoie null si le token est illisible.
export const decodeJwtPayload = (token) => {
  try {
    const part = String(token).split(".")[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const json = decodeURIComponent(
      atob(padded)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch (_) {
    return null;
  }
};

export const isTokenValid = (token, nowMs = Date.now()) => {
  if (!token) return false;
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== "number") return false;
  return payload.exp * 1000 > nowMs;
};

export const authProvider = {
  login: async ({ username, password }) => {
    clearSession();
    let res;
    try {
      res = await fetch(`${API_BASE_URL}/${LOGIN_PATH}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-React-Admin": "true",
        },
        body: JSON.stringify({ email: (username || "").trim(), password: password || "" }),
      });
    } catch (_) {
      throw new Error(NETWORK_ERROR_MESSAGE);
    }
    if (res.status === 429) throw new Error(TOO_MANY_ATTEMPTS_MESSAGE);
    if (res.status === 400 || res.status === 401 || res.status === 403) {
      throw new Error(INVALID_CREDENTIALS_MESSAGE);
    }
    if (!res.ok) {
      throw new Error("Erreur d'authentification. Réessayez.");
    }
    let json = null;
    try {
      json = await res.json();
    } catch (_) {}
    if (!json || !json.token || !isTokenValid(json.token)) {
      throw new Error("Réponse d'authentification invalide. Réessayez.");
    }
    persistIdentity(json.email || (username || "").trim().toLowerCase(), json.token);
    return Promise.resolve();
  },

  logout: () => {
    clearSession();
    return Promise.resolve();
  },

  checkAuth: () => {
    if (isTokenValid(getToken())) return Promise.resolve();
    clearSession();
    return Promise.reject({ redirectTo: "/login" });
  },

  checkError: (error) => {
    const status = error?.status || error?.response?.status;
    if (status === 401) {
      clearSession();
      return Promise.reject({ redirectTo: "/login", logoutUser: true });
    }
    if (status === 403) {
      return Promise.reject({ message: "Accès refusé", logoutUser: false });
    }
    return Promise.resolve();
  },

  getIdentity: () => {
    const id = readIdentity();
    if (id) return Promise.resolve(id);
    return Promise.resolve({ id: "anonymous", fullName: "Admin" });
  },

  getPermissions: () => {
    const id = readIdentity();
    if (id) return Promise.resolve(id.permissions || id.role || "ADMIN");
    return Promise.resolve("ADMIN");
  },
};

export default authProvider;
